import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LinkAsButton } from "@/components/LinkAsButton";
import { EntityListPage } from "@/components/entity-page/EntityListPage";
import { getFormOptions } from "@/lib/api";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/settings/providers/")({
  component: ProvidersIndexPage,
});

function ProvidersIndexPage() {
  return <ProvidersIndexPageComponent />;
}

// Exported for testing
export function ProvidersIndexPageComponent() {
  const [providers, setProviders] = useState<FormOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getFormOptions("providers")
      .then((options) => {
        if (!active) return;
        setProviders(options);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching providers:", err);
        setError("Failed to load providers.");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <EntityListPage
      title="Providers"
      description="The telephony/voice providers agents can use (e.g. Twilio) and their TwiML behavior - each option here populates an agent's providerId field."
      items={providers}
      loading={loading}
      error={error}
      getItemKey={(provider) => provider.id}
      gridClassName="grid grid-cols-1 gap-4"
      renderItem={(provider) => (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle>{provider.label}</CardTitle>
                <CardDescription>{provider.description || `Value: ${provider.value}`}</CardDescription>
                <p className="text-xs text-muted-foreground mt-1">Order: {provider.order ?? 0}</p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard/settings/providers/$id" params={{ id: provider.id }}>
                  Edit
                </Link>
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}
      createHref="/dashboard/settings/providers/new"
      createLabel="Create Provider"
      emptyMessage="No providers yet. Create one to get started."
      headerActions={
        <LinkAsButton to="/dashboard/settings" variant="ghost" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </LinkAsButton>
      }
    />
  );
}
